import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DateFormatterService } from '@core/services/helpers/date-formatter.service';
import { RealtimeEnvelope } from '@interfaces/realtime/realtime-envelope.interface';
import { Store } from '@ngrx/store';
import { RealtimeService } from '@services/realtime/realtime.service';
import {
  RealtimeChangeNoticeText,
  RealtimePresencePerson,
  summarizeOtherPeople,
} from './realtime-presence-text';
import { selectUserId } from '@state/auth/auth.selectors';
import { AppState } from '@state/index';
import { Observable, Subject, Subscription, map, merge } from 'rxjs';

/**
 * Outra pessoa (ou uma rotina automática) mudou o item que está aberto aqui. `actorName` nulo
 * quando o envelope vem sem autor: rotina sem usuário ou evento entregue ao parceiro, que não
 * recebe nome de quem é da casa. Título pronto em `describeChangeNotice`.
 */
export type RealtimeChangeNotice = RealtimeChangeNoticeText;

/**
 * Acompanha UM tópico com presença (card 3496): `demanda`, `deal de parceiro`, `deal do
 * pipeline`... Provido por componente (`providers: [RealtimeTopicWatcherService]`), então o
 * ciclo de vida acompanha a tela; o `app-realtime-presence-banner` já faz isso.
 *
 * - `watch(topic, changeEventTypes)`: entra no tópico e anuncia presença `viewing`; trocar de
 *   tópico sai do anterior; `watch(null)` sai de tudo.
 * - `setEditing(true|false)`: presença `editing`/`viewing` conforme há alteração não salva.
 * - `people`: quem mais está no tópico (sem o próprio usuário, nem as outras abas dele).
 * - `changeNotice`: a última mudança feita por OUTRA pessoa (um dos `changeEventTypes`).
 * - `changes$`: as mesmas mudanças, para quem prefere reler sozinho em vez de avisar.
 */
@Injectable()
export class RealtimeTopicWatcherService implements OnDestroy {
  private readonly realtime = inject(RealtimeService);
  private readonly dateFormatter = inject(DateFormatterService);
  private readonly store = inject<Store<AppState>>(Store);

  private readonly ownUserId = toSignal(
    this.store.select(selectUserId).pipe(map(id => (id ? String(id) : null))),
    { initialValue: null }
  );

  private readonly peopleSignal = signal<RealtimePresencePerson[]>([]);
  readonly people = this.peopleSignal.asReadonly();

  private readonly changeNoticeSignal = signal<RealtimeChangeNotice | null>(null);
  readonly changeNotice = this.changeNoticeSignal.asReadonly();

  private readonly changesSubject = new Subject<RealtimeEnvelope>();
  readonly changes$: Observable<RealtimeEnvelope> = this.changesSubject.asObservable();

  private topic: string | null = null;
  private changeTypesKey = '';
  private editing = false;
  private ignoredChanges = new Set<string>();
  private subscriptions = new Subscription();

  watch(topic: string | null | undefined, changeEventTypes: readonly string[] = []): void {
    const nextTopic = topic || null;
    const nextTypesKey = [...changeEventTypes].sort().join('|');
    if (nextTopic === this.topic && nextTypesKey === this.changeTypesKey) {
      return;
    }

    this.unwatch();
    if (!nextTopic) {
      return;
    }

    this.topic = nextTopic;
    this.changeTypesKey = nextTypesKey;
    this.subscriptions = new Subscription();

    this.subscriptions.add(
      this.realtime.presence$(nextTopic).subscribe(entries => {
        this.peopleSignal.set(summarizeOtherPeople(entries, this.ownUserId()));
      })
    );

    if (changeEventTypes.length) {
      this.subscriptions.add(
        merge(...changeEventTypes.map(type => this.realtime.on(type, nextTopic))).subscribe(
          envelope => this.handleChange(envelope)
        )
      );
    }

    this.realtime.join(nextTopic);
    this.realtime.setPresence(nextTopic, this.editing ? 'editing' : 'viewing');
  }

  setEditing(editing: boolean): void {
    this.editing = editing;
    if (this.topic) {
      this.realtime.setPresence(this.topic, editing ? 'editing' : 'viewing');
    }
  }

  /**
   * Valores de `payload.change` que não contam como "outra pessoa mudou o item" (card 3878): mudança que tem
   * tela própria, como o Briefing do deal, cujas fases chegam pelo mesmo evento do deal e acenderiam o aviso
   * "Este deal foi atualizado" a cada fase do agente.
   */
  setIgnoredChanges(changes: readonly string[]): void {
    this.ignoredChanges = new Set(changes);
  }

  clearChangeNotice(): void {
    this.changeNoticeSignal.set(null);
  }

  ngOnDestroy(): void {
    this.unwatch();
    this.changesSubject.complete();
  }

  private unwatch(): void {
    this.subscriptions.unsubscribe();
    if (this.topic) {
      this.realtime.leave(this.topic);
    }
    this.topic = null;
    this.changeTypesKey = '';
    this.peopleSignal.set([]);
    this.changeNoticeSignal.set(null);
  }

  private handleChange(envelope: RealtimeEnvelope): void {
    const ownUserId = this.ownUserId();
    if (envelope.actor?.userId && ownUserId && envelope.actor.userId === ownUserId) {
      // Feito por esta pessoa (nesta ou em outra aba): nada a avisar.
      return;
    }
    const change = (envelope.payload as { change?: unknown } | null)?.change;
    if (typeof change === 'string' && this.ignoredChanges.has(change)) {
      return;
    }
    this.changeNoticeSignal.set({
      actorName: envelope.actor?.name?.trim() || null,
      time: this.dateFormatter.formatDateTimeInFormat(envelope.at, 'HH:mm'),
    });
    this.changesSubject.next(envelope);
  }
}
